import {
  Button,
  Heading,
  Image,
  List,
  Text,
  UpgradeDialog,
} from '@buffer-mono/popcorn'
import {
  BufferTrackerReact as BufferTracker,
  Client,
} from '@buffer-mono/tracking-plan'
import type * as React from 'react'
import { useState } from 'react'

import { useOrganizationId } from '~publish/contexts/AccountContext'
import styles from './SuggestionButtons.module.css'

type CtaOptions = {
  location: string
  button: string
}

type SuggestionsPaywallProps = {
  ctaProperties: CtaOptions
  children: React.ReactNode
}

export function SuggestionsPaywall({
  children,
  ctaProperties,
}: SuggestionsPaywallProps): JSX.Element {
  const currentOrganizationId = useOrganizationId()
  const [open, setOpen] = useState(false)

  const view = 'comments'

  const { location, button } = ctaProperties
  const cta = `publish-${view}-${location}-${button}-1` as const

  const upgradePathName = 'comments-suggestions-paywall'

  const SUGGESTIONS_PAYWALL_DATA = {
    heading: 'Upgrade to generate more reply suggestions',
    body: 'You’ve reached the free plan limit of 5 reply suggestions this week. Unlock unlimited suggestions and so much more by upgrading your plan.',
    bulletPoints: [
      'Unlimited reply suggestions',
      'More saved replies for your comments',
      'Schedule up to 2000 posts at a time',
      'Advanced publishing and planning features',
      'In-depth social analytics and reposting',
    ],
    ctaText: 'See Upgrade Options',
  }

  const onCTAClick = (): void => {
    setOpen(false)
    window?.appshell.actions.openModal(window?.appshell.MODALS.planSelector, {
      cta,
      upgradePathName,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error - FIXME: Object literal may only specify known properties, and 'planSelector' does not exist in type 'ModalData'
      planSelector: {
        initialStep: 'upgrade',
      },
    })
  }

  const handleOpen = (isOpen: boolean): void => {
    if (isOpen) {
      BufferTracker.cTAClicked({
        clientName: Client.PublishWeb,
        product: 'publish',
        cta,
        upgradePathName,
        organizationId: currentOrganizationId ?? '',
      })
      BufferTracker.upgradePathViewed({
        clientName: Client.PublishWeb,
        product: 'publish',
        organizationId: currentOrganizationId ?? '',
        upgradePathName,
      })
      // Send event to Clarity to easily track errors in the community
      if (window.clarity) {
        window.clarity('set', 'community_comment_suggestions_paywall_viewed')
      }
    }
    setOpen(isOpen)
  }

  return (
    <UpgradeDialog open={open} onOpenChange={handleOpen}>
      <UpgradeDialog.Trigger>{children}</UpgradeDialog.Trigger>
      <UpgradeDialog.Content size="xlarge">
        <UpgradeDialog.TextContent>
          <Heading size="large">{SUGGESTIONS_PAYWALL_DATA.heading}</Heading>
          <Text>{SUGGESTIONS_PAYWALL_DATA.body}</Text>
          <List className={styles.paywallList}>
            {SUGGESTIONS_PAYWALL_DATA.bulletPoints.map((item, index) => {
              return (
                <List.Item key={`suggestions-paywall-details-${index}`}>
                  {item}
                </List.Item>
              )
            })}
          </List>
          <Button variant="primary" size="large" onClick={onCTAClick}>
            {SUGGESTIONS_PAYWALL_DATA.ctaText}
          </Button>
        </UpgradeDialog.TextContent>
        <UpgradeDialog.Image>
          <Image
            src="https://buffer-publish.s3.amazonaws.com/images/Upgrade-Comments-Suggestions/upgrade-comments-suggestions.png"
            alt="Upgrade to have unlimited reply suggestions"
            placeholder="https://buffer-publish.s3.amazonaws.com/images/Upgrade-Comments-Suggestions/upgrade-comments-suggestions.png"
            className={styles.paywallImage}
          />
        </UpgradeDialog.Image>
      </UpgradeDialog.Content>
    </UpgradeDialog>
  )
}
